import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  Alert,
} from "@mui/material";
import { useState } from "react";
import { useAlert } from "../../context/AlertContext";
import { useTranscriptRequest } from "../../hooks/useTranscription";
import { useTranslation } from "react-i18next";

export interface TranscriptRequestDialogProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  customerId: string;
  interactionId: string;
  hasExistingTranscript?: boolean;
  onTranscriptionStarted?: (jobId: string) => void;
}

const TranscriptRequestDialog = ({
  isOpen,
  setIsOpen,
  customerId,
  interactionId,
  hasExistingTranscript = false,
  onTranscriptionStarted,
}: TranscriptRequestDialogProps) => {
  const { t } = useTranslation(["transcription"]);
  const [financial, setFinancial] = useState<boolean>(false);
  const [personal, setPersonal] = useState<boolean>(false);
  const showAlert = useAlert();
  const { mutateAsync: requestTranscript, isPending } = useTranscriptRequest();

  const handleClose = () => {
    setIsOpen(false);
    setFinancial(false);
    setPersonal(false);
  };

  const handleSubmit = async () => {
    const hasRedactionOptions = financial || personal;

    try {
      const response = await requestTranscript({
        customerId,
        interactionId,
        redactionOptions: hasRedactionOptions
          ? {
              enabled: true,
              piiToRedact: {
                financial,
                personal,
              },
            }
          : {
              enabled: false,
              piiToRedact: {
                financial: false,
                personal: false,
              },
            },
      });

      const message = hasRedactionOptions
        ? t("transcription:redactedTranscriptRequestSubmitted")
        : t("transcription:transcriptRequestSubmitted");

      showAlert(message, "success");

      if (onTranscriptionStarted && response.jobId) {
        onTranscriptionStarted(response.jobId);
      }

      handleClose();
    } catch (error: unknown) {
      console.error(error);
      if (error instanceof Error) {
        showAlert(error.message, "error");
      } else {
        showAlert(t("transcription:failedToRequestTranscript"), "error");
      }
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("transcription:transcriptionOptions")}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t("transcription:transcriptionOptionsDescription")}
        </Typography>

        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={financial}
                onChange={(e) => setFinancial(e.target.checked)}
                disabled={isPending}
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  {t("transcription:financialRedactionOption")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("transcription:financialRedactionDescription")}
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={personal}
                onChange={(e) => setPersonal(e.target.checked)}
                disabled={isPending}
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  {t("transcription:personalRedactionOption")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("transcription:personalRedactionDescription")}
                </Typography>
              </Box>
            }
          />
        </FormGroup>

        {!financial && !personal && (
          <Alert
            severity={hasExistingTranscript ? "warning" : "info"}
            sx={{ mt: 2 }}
          >
            {hasExistingTranscript
              ? t("transcription:mustSelectRedactionOption")
              : t("transcription:noRedactionOptionsSelected")}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isPending}>
          {t("common:cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            isPending || (hasExistingTranscript && !financial && !personal)
          }
        >
          {isPending
            ? t("transcription:transcriptRequestInProgress")
            : t("transcription:requestTranscript")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TranscriptRequestDialog;
